// Auth.jsx (UI-only: LoginPage + AuthGate)
// Note: Authentication state and actions are provided by src/context/AuthContext

import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import { Routes, Route, useLocation } from 'react-router-dom';
import { LoadingSpinner, FormInput, PasswordInput } from './Common.jsx';
import PlatformLayout from './PlatformLayout.jsx';
import { useAuth } from '../context/useAuth';
import PropTypes from 'prop-types';
import { ROLES } from '../utils/roles';
import { apiGet, saveTokens } from '../api';

// Simple Email Regex for client-side validation
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

import { AlertCircle, CheckCircle2 } from 'lucide-react'; // NEW IMPORT

// --- Page Imports ---
import AdminDashboardPage from '../pages/AdminDashboardPage';
import PartnersPage from '../pages/PartnersPage';
import PartnerDetailPage from '../pages/PartnerDetailPage';
import TenantsPage from '../pages/TenantsPage';
import TenantDetailPage from '../pages/TenantDetailPage';
import UsersPage from '../pages/UsersPage';
import RolesPermissionsPage from '../pages/RolesPermissionsPage';
import BusinessAppsPage from '../pages/BusinessAppsPage';
import DataModelsPage from '../pages/DataModelsPage.tsx'; // NEW: Canvas-based designer

import DatasetsPage from '../pages/DatasetsPage';
import DashboardViewPage from '../pages/dashboard/DashboardViewPage';
import DashboardsPage from '../pages/dashboard/DashboardsPage';
import DashboardsLandingPage from '../pages/dashboard/DashboardsLandingPage';
import SettingsPage from '../pages/SettingsPage';
import AlertRulesPage from '../pages/AlertRulesPage';
import MLAppRunnerPage from '../pages/MLAppRunnerPage';
import CreditsPage from '../pages/CreditsPage';
import ProfilePage from '../pages/ProfilePage';

const LoginPage = ({ onLogin, onRequestMagicLink, onMagicLogin, loginWithToken }) => {
    const { verifyMfa, resendMfa } = useAuth(); // Need verifyMfa and resendMfa from context <== CHANGED
    const location = useLocation();

    // Check for URL Token immediately
    const initialToken = new URLSearchParams(location.search).get('token');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // Initialize loading/message if token is present
    const [message, setMessage] = useState(initialToken ? "Verifying magic link..." : '');
    const [isLoading, setIsLoading] = useState(!!initialToken);

    // Dedicated state to hide form during URL token verification
    const [isVerifyingUrlToken, setIsVerifyingUrlToken] = useState(!!initialToken);

    const [magicLinkSent, setMagicLinkSent] = useState(false);

    // Auth Method Toggle: 'password' | 'magic'
    const [authMethod, setAuthMethod] = useState('password');

    // --- MFA State ---
    const [showMfaInput, setShowMfaInput] = useState(false);
    const [mfaSessionToken, setMfaSessionToken] = useState(null);
    const [otpCode, setOtpCode] = useState('');

    // --- Password Expired Lockout State ---
    const [passwordExpired, setPasswordExpired] = useState(false);
    const [resetLinkSent, setResetLinkSent] = useState(false);

    // --- MFA RESEND ---
    const [resendTimer, setResendTimer] = useState(30);
    const [isResending, setIsResending] = useState(false);

    useEffect(() => {
        let interval;
        if (showMfaInput && resendTimer > 0) {
            interval = setInterval(() => setResendTimer(prev => prev - 1), 1000);
        }
        return () => clearInterval(interval);
    }, [showMfaInput, resendTimer]);

    const handleResendCode = async () => {
        setIsResending(true);
        try {
            await resendMfa(mfaSessionToken);
            setMessage("Code resent successfully!");
            setResendTimer(30);
        } catch (err) {
            setMessage(err.message || "Failed to resend code.");
        } finally {
            setIsResending(false);
        }
    };

    // Handle URL Magic Token

    const processedTokenRef = React.useRef(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        // Prevent double-processing (React StrictMode fix)
        if (token && processedTokenRef.current !== token) {
            processedTokenRef.current = token;
            handleMagicLogin(token);

            // Clean URL history to remove token
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [location]);

    const handleMagicLogin = async (token) => {
        setIsLoading(true);
        setIsVerifyingUrlToken(true);
        setMessage("Verifying magic link...");
        try {
            await onMagicLogin(token);
        } catch (error) {
            if (error.isSsaPending) {
                // Show the same SSA waiting screen as password login
                setSsaRequestId(error.requestId);
                setSsaStatus('pending');
                setSsaTimeout(error.timeout || 60);
                setIsVerifyingUrlToken(false);
            } else {
                setMessage("Invalid or expired magic link.");
                setIsVerifyingUrlToken(false); // Reveal form on error
            }
            setIsLoading(false);
        }
    };

    const [ssaRequestId, setSsaRequestId] = useState(null);
    const [ssaStatus, setSsaStatus] = useState('pending'); // pending | approved | rejected | expired
    const [ssaTimeout, setSsaTimeout] = useState(30);

    const handlePasswordLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');
        try {
            await onLogin(email, password);
        } catch (error) {
            // Check for SSA Pending Requirement
            if (error.isSsaPending) {
                // Stamp sessionStorage so SessionRevokedModal knows this window is the
                // REQUESTER for this request_id and should NOT show the Allow/Block modal.
                sessionStorage.setItem('ssa_pending_request', error.requestId);
                setSsaRequestId(error.requestId);
                setSsaStatus('pending');
                setSsaTimeout(error.timeout || 30);
                setMessage('');
            }
            // Check for MFA Requirement
            else if (error.isMfaRequired) {
                setMfaSessionToken(error.mfaSessionToken);
                setShowMfaInput(true);
                setMessage(`Enter the code sent to your ${error.mfaMethod || 'email'}`);
            }
            // Check for Password Expiry
            else if (error.isPasswordExpired) {
                setPasswordExpired(true);
                setMessage('');
            } else {
                setMessage(error.message || "Login failed.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    // SSA Status Polling
    useEffect(() => {
        let pollInterval;
        let countdownInterval;
        if (ssaRequestId && ssaStatus === 'pending') {
            countdownInterval = setInterval(() => {
                setSsaTimeout(prev => (prev > 0 ? prev - 1 : 0));
            }, 1000);

            pollInterval = setInterval(async () => {
                try {
                    const res = await apiGet(`/auth/sessions/ssa-status/${ssaRequestId}`);
                    if (res.status === 'approved') {
                        setSsaStatus('approved');
                        sessionStorage.removeItem('ssa_pending_request');
                        // Success! Save tokens and trigger login
                        saveTokens(res.access_token, res.refresh_token);
                        if (res.session_id) {
                            sessionStorage.setItem("auth_sid", res.session_id);
                        }
                        // loginWithToken is a helper in useAuth that just updates context state
                        if (loginWithToken) {
                            await loginWithToken(res.access_token, res.session_id);
                        }
                        setSsaRequestId(null);
                    } else if (res.status === 'rejected') {
                        setSsaStatus('rejected');
                        sessionStorage.removeItem('ssa_pending_request');
                        setSsaRequestId(null); // Fully reset
                        setMessage("Login rejected by the existing session.");
                        clearInterval(pollInterval);
                    } else if (res.status === 'expired') {
                        setSsaStatus('expired');
                        sessionStorage.removeItem('ssa_pending_request');
                        setSsaRequestId(null); // Fully reset
                        setMessage("No response from the existing session. Please try again.");
                        clearInterval(pollInterval);
                    }
                } catch (err) {
                    console.error("SSA Polling error:", err);
                }
            }, 2000);
        }
        return () => {
            if (pollInterval) clearInterval(pollInterval);
            if (countdownInterval) clearInterval(countdownInterval);
        };
    }, [ssaRequestId, ssaStatus, loginWithToken]);

    // --- RENDER SSA PENDING ---
    if (ssaRequestId && ssaStatus === 'pending') {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center relative">
                <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-md rounded-lg p-8 text-center">
                    <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 p-3 rounded-full inline-flex mb-4">
                        <CircularProgress size={32} color="inherit" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Awaiting Approval</h1>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">
                        You are already logged in on another device.
                    </p>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 font-medium">
                        Waiting for the existing session to approve your login attempt...
                    </p>
                    {ssaTimeout > 0 && (
                        <p className="mt-4 text-xs text-gray-400 uppercase tracking-widest">
                            Request expires in <span className="font-mono font-bold text-primary">{ssaTimeout}s</span>
                        </p>
                    )}
                    <div className="mt-8">
                        <button
                            onClick={() => {
                                setSsaRequestId(null);
                                setSsaStatus('pending');
                            }}
                            className="text-sm text-primary hover:text-primary-dark font-medium hover:underline"
                        >
                            Cancel and go back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // --- RENDER PASSWORD EXPIRED ---
    if (passwordExpired) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center relative">
                <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-md rounded-lg p-8 text-center">
                    {!resetLinkSent ? (
                        <>
                            <div className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-3 rounded-full inline-flex mb-4">
                                <AlertCircle size={32} strokeWidth={2} />
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Password Expired</h1>
                            <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm">
                                Your password has expired in accordance with your organization's security policy.
                                Please request a secure reset link to regain access.
                            </p>
                            <button
                                onClick={async () => {
                                    setIsLoading(true);
                                    try {
                                        await apiFetch("/auth/forgot-password", null, {
                                            method: "POST",
                                            body: JSON.stringify({ email }),
                                        });
                                        setResetLinkSent(true);
                                    } catch (err) {
                                        setMessage("Failed to send reset link.");
                                    } finally {
                                        setIsLoading(false);
                                    }
                                }}
                                disabled={isLoading}
                                className="w-full py-3 px-4 bg-primary hover:bg-primary-dark text-white font-bold rounded-lg shadow-md transition-colors flex justify-center items-center"
                            >
                                {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Send Reset Link'}
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 p-3 rounded-full inline-flex mb-4">
                                <CheckCircle2 size={32} strokeWidth={2} />
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Check Your Email</h1>
                            <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm">
                                We've sent a secure password reset link to <strong>{email}</strong>.
                            </p>
                        </>
                    )}

                    <button
                        onClick={() => {
                            setPasswordExpired(false);
                            setResetLinkSent(false);
                        }}
                        className="mt-6 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 hover:underline inline-block w-full text-center"
                    >
                        Back to Login
                    </button>
                    {message && (
                        <p className="mt-4 text-center text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                            {message}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    const handleMfaVerify = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');
        try {
            await verifyMfa(mfaSessionToken, otpCode);
        } catch (error) {
            setMessage(error.message || "Invalid OTP code.");
        } finally {
            setIsLoading(false);
        }
    }

    const handleMagicLinkRequest = async (e) => {
        e.preventDefault(); // Prevent form submission if button is inside form

        if (!email) {
            setMessage("Please enter your email address.");
            return;
        }
        if (!EMAIL_REGEX.test(email)) {
            setMessage("Please enter a valid email address.");
            return;
        }

        setIsLoading(true);
        setMessage('');
        try {
            await onRequestMagicLink(email);
            setMagicLinkSent(true);
        } catch (error) {
            console.error("[AuthContext] Magic link request failed:", error);
            setMessage(error.message || "Failed to send magic link.");
        } finally {
            setIsLoading(false);
        }
    };

    // --- RENDER URL TOKEN VERIFICATION ---
    if (isVerifyingUrlToken) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center relative">
                <div className="text-center">
                    <LoadingSpinner size={48} />
                    <p className="mt-4 text-gray-500 dark:text-gray-400 font-medium animate-pulse">{message}</p>
                </div>
            </div>
        );
    }

    // --- RENDER MFA INPUT ---
    if (showMfaInput) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center relative">
                <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-md rounded-lg p-8">
                    <div className="text-center mb-8">
                        <div className="bg-primary/10 text-primary p-3 rounded-full inline-flex mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Verify Identity</h1>
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                            {message || "Enter the One-Time Password sent to you."}
                        </p>
                    </div>

                    <form onSubmit={handleMfaVerify} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300 text-center">One-Time Password</label>
                            <input
                                value={otpCode}
                                onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                type="text"
                                placeholder="123456"
                                className="w-full px-4 py-3 text-center text-2xl tracking-widest border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary bg-white dark:bg-gray-700 dark:text-white transition-all shadow-sm font-mono"
                                required
                                autoFocus
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={isLoading || otpCode.length < 6}
                            className="w-full h-11 px-4 py-3 text-sm font-bold text-white bg-primary hover:bg-primary-dark disabled:opacity-70 rounded-lg shadow-lg hover:shadow-primary/30 transition-all flex items-center justify-center"
                        >
                            {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Verify Code'}
                        </button>

                        {/* RESEND LINK */}
                        <div className="text-center">
                            <button
                                type="button"
                                disabled={resendTimer > 0 || isResending}
                                onClick={handleResendCode}
                                className="text-sm text-primary hover:text-primary-dark disabled:text-gray-400 disabled:cursor-not-allowed font-medium hover:underline transition-colors"
                            >
                                {isResending ? 'Sending...' : resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend Code'}
                            </button>
                        </div>

                        <button
                            type="button"
                            onClick={() => setShowMfaInput(false)}
                            className="w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 hover:underline"
                        >
                            Back to Login
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center relative">

            {/* Magic Link Success Popup */}
            {magicLinkSent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center relative">

                        <button
                            onClick={() => setMagicLinkSent(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            aria-label="Close dialog"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>

                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                            Check your email
                        </h3>

                        <p className="text-gray-600 dark:text-gray-300 mb-2">
                            We’ve sent a secure login link to your registered email address.
                        </p>

                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                            The link will expire in 15 minutes.
                        </p>

                        <button
                            onClick={() => setMagicLinkSent(false)}
                            className="w-full py-2.5 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg shadow-md transition-colors"
                        >
                            OK, got it
                        </button>
                    </div>
                </div>
            )}

            {/* Login Card */}
            <div className="max-w-lg w-full bg-white dark:bg-gray-800 shadow-md rounded-lg p-8">
                <div className="text-center mb-8">
                    <img src="/aidas_logo.png" alt="AIDAS Logo" className="w-auto h-16 mx-auto mb-4" />
                    <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
                        AIDAS Platform
                    </h1>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        Sign in to access your account
                    </p>
                </div>

                <form
                    onSubmit={e => {
                        e.preventDefault();
                        if (authMethod === 'password') {
                            handlePasswordLogin(e);
                        } else {
                            handleMagicLinkRequest(e);
                        }
                    }}
                    className="space-y-6"
                >

                    {/* Email */}
                    <FormInput
                        id="email"
                        label="Email address"
                        name="email"
                        autoComplete="username"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        type="email"
                        required
                        className="w-full"
                    />

                    {/* Auth Method */}
                    <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                            Sign in with
                        </label>
                        <div className="flex bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
                            <button
                                type="button"
                                onClick={() => setAuthMethod('password')}
                                className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all ${authMethod === 'password'
                                    ? 'bg-white dark:bg-gray-600 text-primary shadow-sm'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                    }`}
                            >
                                Password
                            </button>
                            <button
                                type="button"
                                onClick={() => setAuthMethod('magic')}
                                className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all ${authMethod === 'magic'
                                    ? 'bg-white dark:bg-gray-600 text-primary shadow-sm'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                    }`}
                            >
                                Email link
                            </button>
                        </div>
                    </div>

                    {/* Auth Actions */}
                    {authMethod === 'password' ? (
                        <div className="space-y-4">
                            <PasswordInput
                                id="password"
                                label="Password"
                                name="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                                className="w-full"
                            />

                            <button
                                type="submit"
                                onClick={handlePasswordLogin}
                                disabled={isLoading || !email || !password}
                                className="w-full h-11 px-4 py-3 text-sm font-bold text-white bg-primary hover:bg-primary-dark disabled:opacity-70 disabled:cursor-not-allowed rounded-lg shadow-lg transition-all flex items-center justify-center"
                            >
                                {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Sign in'}
                            </button>
                        </div>
                    ) : (
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 bg-blue-50 dark:bg-blue-900/10 p-3 rounded-lg border border-blue-100 dark:border-blue-800">
                                We’ll send a secure, one-time login link to <strong>{email || 'your email'}</strong>.
                                No password required.
                            </p>

                            <button
                                type="submit"
                                onClick={handleMagicLinkRequest}
                                disabled={isLoading || !email || !EMAIL_REGEX.test(email)}
                                className="w-full h-11 px-4 py-3 text-sm font-bold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-70 disabled:cursor-not-allowed rounded-lg shadow-sm transition-all flex items-center justify-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Send login link'}
                            </button>
                        </div>
                    )}
                </form>

                <div className="h-[24px] mt-6">
                    {message && (
                        <p className="text-center text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                            {message}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );

};

LoginPage.propTypes = {
    onLogin: PropTypes.func.isRequired,
    onRequestMagicLink: PropTypes.func.isRequired,
    onMagicLogin: PropTypes.func.isRequired,
    loginWithToken: PropTypes.func.isRequired,
};

export const AuthGate = () => {
    const { user, token, login, requestMagicLink, loginWithMagicToken, loginWithToken, loading, logout } = useAuth();
    const location = useLocation();

    // Check for Magic Link Token in URL
    const params = new URLSearchParams(location.search);
    const magicToken = params.get('token');

    if (loading) return <div className="h-screen w-screen flex items-center justify-center"><LoadingSpinner size={40} /></div>;

    // IF Magic Token is present, PRIORITIZE LoginPage to handle it (even if user is logged in)
    // This allows switching users via magic link.
    if (magicToken || !token || !user) {
        if (!magicToken && (token || user)) {
            console.debug("[AuthGate] Transient state detected - showing LoginPage?", { hasToken: !!token, hasUser: !!user });
        }
        return <LoginPage onLogin={login} onRequestMagicLink={requestMagicLink} onMagicLogin={loginWithMagicToken} loginWithToken={loginWithToken} />;
    }

    // Role-Based Layout Redirect
    // All roles now use PlatformLayout (Unified Portal)
    return (
        <PlatformLayout onLogout={logout} token={token}>
            <Routes>
                <Route path="/" element={<AdminDashboardPage />} />
                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/partners/:id" element={<PartnerDetailPage />} />
                <Route path="/tenants" element={<TenantsPage />} />
                <Route path="/tenants/:id" element={<TenantDetailPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/roles" element={<RolesPermissionsPage />} />
                <Route path="/apps" element={<BusinessAppsPage />} />
                <Route path="/datamodels" element={<DataModelsPage />} />
                <Route path="/datasets" element={<DatasetsPage />} />
                <Route path="/datasets" element={<DatasetsPage />} />
                <Route path="/dashboards" element={<DashboardsLandingPage />} />
                <Route path="/dashboards/create" element={<DashboardsPage />} />
                <Route path="/dashboards/:id/view" element={<DashboardViewPage />} />
                <Route path="/dashboards/:id/edit" element={<DashboardsPage />} />
                <Route path="/platform/app-runner/:contentId" element={<MLAppRunnerPage />} />
                <Route path="/dashboards/:id" element={<DashboardViewPage />} />
                <Route path="/alert-rules" element={<AlertRulesPage token={token} currentUser={user} />} />
                <Route path="/credits" element={<CreditsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
            </Routes>
        </PlatformLayout>
    );
};

export default AuthGate;
